import type { CustomerSalesSummary } from './api';
import { DetayKart } from './DetayKart';
import { durumAdi, para, tamSayi, tarih, ORTAK } from './metin/ortak';
import { KABUK_METIN } from './metin/kabuk';
import { baglantiTiklamasi, siparisYolu, YOLLAR, type Yol } from './yonlendirme';

/**
 * Müşteri detayında "Satış özeti" (03.11.2026): üstte tek kart, altında teklif ve sipariş listesi.
 * Ürün Sahibi: "teklifler müşteriye bağlanmalı ... müşterilerimizin ne kadar harcama yaptığını
 * oradan görürüz." Hesap sunucuda (`customer-sales-summary.ts`); burada yalnız çizim.
 * Para birimleri ayrı satırda, çevirme yok. Finans izni yoksa sunucu tutarları null döner.
 * Düzen (03.11.2026): üç kart (özet şeridi ve harcama, teklifler, siparişler); listeler tablo yerine
 * satır listesi, böylece 390 pikselde yana kaydırma gerekmez. Hesap, test kimlikleri ve bağlantılar aynı.
 */

/** Hızlı tatil teklifi kendi ekranında, ötekiler satış ekranında açılır (teklif için ayrı sayfa adresi yok). */
function teklifAdresi(teklif: CustomerSalesSummary['quoteItems'][number], musteriKimligi: string): Yol {
  return teklif.hizli
    ? `${YOLLAR.hizliTeklif}?musteri=${encodeURIComponent(musteriKimligi)}`
    : `${YOLLAR.satis}?teklif=${encodeURIComponent(teklif.code)}`;
}

export function MusteriSatisOzeti({ ozet, git }: { ozet: CustomerSalesSummary; git: (hedef: Yol) => void }) {
  const M = KABUK_METIN.musteriDetay;
  const t = ozet.quotes;
  const yeniTeklif = `${YOLLAR.hizliTeklif}?musteri=${encodeURIComponent(ozet.customerId)}`;
  return (
    <>
      <DetayKart kimlik="customer-sales-summary" baslik={M.satisOzetiBasligi} testKimligi="customer-sales-summary">
        <div className="stat-grid detay-seritler">
          <div className="stat">
            <span className="stat-label">{M.toplamTeklif}</span>
            <p className="stat-value" data-testid="sales-quote-total">{tamSayi(t.total)}</p>
          </div>
          <div className="stat">
            <span className="stat-label">{M.kabulEdilen}</span>
            <p className="stat-value" data-testid="sales-quote-accepted">{tamSayi(t.accepted)}</p>
          </div>
          <div className="stat" title={M.kabulOraniIpucu}>
            <span className="stat-label">{M.kabulOrani}</span>
            <p className="stat-value" data-testid="sales-acceptance-rate">{t.acceptanceRateBps === null ? M.kabulOraniYok : M.oranYaz(t.acceptanceRateBps)}</p>
          </div>
          <div className="stat">
            <span className="stat-label">{M.acikTeklif}</span>
            <p className="stat-value" data-testid="sales-quote-open">{tamSayi(t.open)}</p>
          </div>
          <div className="stat">
            <span className="stat-label">{M.reddedilenSuresiDolan}</span>
            <p className="stat-value" data-testid="sales-quote-lost">{tamSayi(t.rejected)} / {tamSayi(t.expired)}</p>
          </div>
          <div className="stat" title={M.basariliSatisIpucu}>
            <span className="stat-label">{M.basariliSatis}</span>
            <p className="stat-value" data-testid="sales-successful">{tamSayi(ozet.successfulSales)}</p>
          </div>
          <div className="stat">
            <span className="stat-label">{M.ilkSatis}</span>
            <p className="stat-value">{tarih(ozet.firstSaleAt)}</p>
          </div>
          <div className="stat">
            <span className="stat-label">{M.sonSatis}</span>
            <p className="stat-value">{tarih(ozet.lastSaleAt)}</p>
          </div>
        </div>

        <h4 className="detay-alt-baslik">{M.harcamaBasligi}</h4>
        {ozet.spending === null ? (
          <p className="muted" data-testid="sales-spending-hidden">{M.harcamaGizli}</p>
        ) : ozet.spending.length === 0 ? (
          <p className="muted" data-testid="sales-spending-empty">{M.harcamaYok}</p>
        ) : (
          <div className="table-wrap">
            <p className="muted">{M.harcamaIpucu}</p>
            <table data-testid="sales-spending">
              <thead>
                <tr>
                  <th>{M.sutunParaBirimi}</th>
                  <th>{M.sutunTahsil}</th>
                  <th>{M.sutunKabulSatis}</th>
                  <th>{M.sutunSiparisSayisi}</th>
                  <th>{M.sutunOrtalama}</th>
                </tr>
              </thead>
              <tbody>
                {ozet.spending.map((satir) => (
                  <tr key={satir.currencyCode} data-testid="sales-spending-row">
                    <td>{satir.currencyCode}</td>
                    <td>{para(satir.collectedMinor, satir.currencyCode)}</td>
                    <td>{para(satir.soldMinor, satir.currencyCode)}</td>
                    <td>{tamSayi(satir.orderCount)}</td>
                    <td>{para(satir.averageOrderMinor, satir.currencyCode)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DetayKart>

      <DetayKart kimlik="customer-quotes-card" baslik={M.tekliflerBasligi}>
        {ozet.quoteItems.length === 0 ? (
          <div className="detay-bos">
            <strong>{M.teklifYok}</strong>
            <a href={yeniTeklif} onClick={baglantiTiklamasi(git, yeniTeklif)} data-testid="customer-quote-empty-action">
              {M.teklifBosEylem}
            </a>
          </div>
        ) : (
          <ul className="detay-liste detay-satirlar" data-testid="customer-quotes">
            {ozet.quoteItems.map((teklif) => {
              const adres = teklifAdresi(teklif, ozet.customerId);
              return (
                <li key={teklif.id} className="detay-satir">
                  <div className="detay-satir-ana">
                    <strong>{teklif.code}</strong>
                    <span className="muted">{teklif.title}</span>
                  </div>
                  <div className="detay-satir-meta">
                    <span className="state">{durumAdi(teklif.status)}</span>
                    <small className="muted">{tarih(teklif.createdAt)}</small>
                  </div>
                  <div className="detay-satir-tutar">{para(teklif.totalMinor, teklif.currencyCode)}</div>
                  <a href={adres} onClick={baglantiTiklamasi(git, adres)} data-testid="customer-quote-link">
                    {M.teklifiAc}
                  </a>
                </li>
              );
            })}
          </ul>
        )}
      </DetayKart>

      <DetayKart kimlik="customer-orders-card" baslik={M.siparislerBasligi}>
        {ozet.orderItems.length === 0 ? (
          <div className="detay-bos">
            <strong>{M.siparisYok}</strong>
            <span className="muted">{M.siparisBosYardim}</span>
          </div>
        ) : (
          <ul className="detay-liste detay-satirlar" data-testid="customer-orders">
            {ozet.orderItems.map((siparis) => (
              <li key={siparis.id} className="detay-satir">
                <div className="detay-satir-ana">
                  <strong>{siparis.code}</strong>
                  <span className="muted">{tarih(siparis.createdAt)}</span>
                </div>
                <div className="detay-satir-meta">
                  <span className="state">{durumAdi(siparis.status)}</span>
                  <small className="muted">{M.odemeDurumu[siparis.paymentState] ?? siparis.paymentState}</small>
                </div>
                <div className="detay-satir-tutar">
                  {siparis.revenueMinor === null ? ORTAK.belirtilmemis : para(siparis.revenueMinor, siparis.currencyCode)}
                  <small className="muted">
                    {M.sutunTahsilat}: {siparis.collectedMinor === null ? ORTAK.belirtilmemis : para(siparis.collectedMinor, siparis.currencyCode)}
                  </small>
                </div>
                <a href={siparisYolu(siparis.id)} onClick={baglantiTiklamasi(git, siparisYolu(siparis.id))} data-testid="customer-order-link">
                  {M.siparisiAc}
                </a>
              </li>
            ))}
          </ul>
        )}
      </DetayKart>
    </>
  );
}
